<template lang="pug">
Tabs.cash-flow-log-list.pagination
    el-card.contains-table(shadow="always")
        .error(v-if="error")
            template(v-if="error.code")
                Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
            template(v-else)
                Error(:message="$t('Something went wrong!')" :code="501")
        .cash-flow-logs-table(v-else)
            paginated-items(
                scope="ex"
                type="cash_flow_log"
                :loading="loading"
                :items="cashFlowLogsPaginated.items"
                :filters="filters"
                :sortables="sortables"
                :show-filters="showFilters"
                :variables="cashFlowLogsPaginated.variables"
                :all-columns="cashFlowLogsPaginated.allColumns"
                :active-columns="cashFlowLogsPaginated.activeColumns"
                :page-info="cashFlowLogsPaginated.pageInfo"
                :sort="sort"
                @pagination-size-change="handleSizeChange"
                @pagination-current-change="handleCurrentChange"
                @show-table-columns="handleColsShow"
                @change-filter="handleFilterInputChange"
                @change-sort="handleSortInputChange"
                @click="handleClick"
                :row-class="rowClass"
            )
                template(slot="top")
                    el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                        .row
                            .left
                            .middle.hide-on-empty
                                span.info
                                    | {{ $t('Found {total} cash movements', { total: cashFlowLogsPaginated.pageInfo.total }) }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-file-csv fas"
                                    @click="openExportDialog"
                                ) {{ $t('Export cash movements...') }}
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisiblity"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}

                template(v-slot:amount="{ item, value }")
                    span(:class="['amount', item.direction]")
                        | {{ value | moneyFormat(item.currency_code) }}

                template(v-slot:direction="{ item, value }")
                    span.direction(v-if="isCashReport(item)" :class="value")
                        i.fas.el-icon-fa-check
                        | &nbsp;{{ $t('Total Amount') }}
                    span.direction(v-else :class="value")
                        i.fas(:class="value === 'out' ? 'el-icon-fa-arrow-up' : 'el-icon-fa-arrow-down'")
                        | &nbsp;{{ $t(value) }}

                template(v-slot:cash_report_id="{ item }")
                    a.report(
                        v-if="item.cashReport && item.cashReport.file"
                        href
                        :disabled="printing"
                        @click.stop.prevent="printCashReport(item.cashReport)"
                    )
                        i.fas.el-icon-fa-file-pdf
                        | &nbsp;\#{{ item.cashReport.id }}
                    span.gray(v-else-if="item.cashReport") \#{{ item.cashReport.id }}

                template(v-slot:purchase="{ item }")
                    a(
                        v-if="item.purchase && item.purchase.id"
                        href
                        @click.stop.prevent="goToPurchase(item.purchase.id)"
                    )
                        | \#{{ item.purchase.id }}

    el-dialog.cash-flow-log-export-dialog(
        :title="$t('Select fields to export')"
        :visible.sync="exportDialog.visible"
        :append-to-body="true"
        :close-on-click-modal="false"
        width="540px"
    )
        .export-fields(v-loading="exportDialog.loading")
            .field-section-label {{ $t('Export format') }}
            .export-format
                el-radio-group(v-model="exportDialog.format" size="mini")
                    el-radio-button(label="standard") {{ $t('Standard (fields)') }}
                    el-radio-button(label="datev") {{ $t('DATEV (summarized)') }}

            .field-section-label {{ $t('Time period') }}
            .date-range
                el-radio-group(v-model="exportDialog.dateRange" size="mini")
                    el-radio-button(label="all") {{ $t('All') }}
                    el-radio-button(label="month") {{ $t('Month') }}
                el-date-picker(
                    v-if="exportDialog.dateRange === 'month'"
                    v-model="exportDialog.month"
                    type="month"
                    size="mini"
                    :clearable="false"
                    format="MM.yyyy"
                    :placeholder="$t('Select month')"
                    :picker-options="monthPickerOptions"
                )

            template(v-if="exportDialog.format === 'standard'")
                .field-section-header
                    .field-section-label {{ $t('Fields') }}
                    el-button(type="text" size="mini" @click="toggleAllExportFields") {{ allExportFieldsSelected ? $t('Deselect all') : $t('Select all') }}
                el-checkbox-group.field-chips(v-model="exportDialog.fields")
                    el-checkbox(
                        v-for="field in exportDialog.basicFields"
                        :key="field.key"
                        :label="field.key"
                        border
                        size="mini"
                    ) {{ $t(field.label) }}
            .datev-hint(v-else)
                i.fas.el-icon-fa-circle-info
                | &nbsp;{{ $t('DATEV export: one summarized booking per day and account.') }}
        template(slot="footer")
            el-button(size="mini" icon="el-icon-fa-times fas" @click="exportDialog.visible = false") {{ $t('Dismiss') }}
            el-button(
                size="mini"
                type="primary"
                icon="el-icon-fa-file-csv fas"
                :loading="exportDialog.processing"
                :disabled="exportDialog.loading || (exportDialog.format === 'standard' && !exportDialog.fields.length)"
                @click="runExport"
            ) {{ $t('Export') }}
</template>

<i18n>
{
    "en": {
        "Found {total} cash movements": "Found {total} cash movements",
        "Total Amount": "Total Amount",
        "in": "In",
        "out": "Out",
        "Export cash movements...": "Export cash movements...",
        "Select fields to export": "Select fields to export",
        "Export format": "Export format",
        "Standard (fields)": "Standard (fields)",
        "DATEV (summarized)": "DATEV (summarized)",
        "DATEV export: one summarized booking per day and account.": "DATEV export: one summarized booking per day and account.",
        "Other": "Other",
        "Time period": "Time period",
        "All": "All",
        "Month": "Month",
        "Select month": "Select month",
        "Fields": "Fields",
        "Select all": "Select all",
        "Deselect all": "Deselect all",
        "Created at": "Created at",
        "Updated at": "Updated at",
        "Description": "Description",
        "Comment": "Comment",
        "Amount": "Amount",
        "Direction": "Direction",
        "Currency": "Currency",
        "Point of sale": "Point of sale",
        "Payment method": "Payment method",
        "Administrator": "Administrator",
        "Cash report": "Cash report",
        "Purchase": "Purchase",
        "No cash movements for {month}.": "No cash movements for {month}."
    },
    "de": {
        "Cash Movements": "Kassenbewegungen",
        "Found {total} cash movements": "{total} Kassenbewegungen gefunden",
        "Total Amount": "Gesamtbetrag",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Cash movements could not be fetched.": "Kassenbewegungen konnten nicht abgerufen werden.",
        "An error occurred!": "Es ist ein Fehler aufgetreten!",
        "Could not print the cash report PDF!": "Kassenabschluss PDF konnte nicht gedruckt werden!",
        "in": "Einzahlung",
        "out": "Auszahlung",
        "Export cash movements...": "Kassenbewegungen exportieren...",
        "Select fields to export": "Exportfelder auswählen",
        "Export format": "Export-Format",
        "Standard (fields)": "Standard (Felder)",
        "DATEV (summarized)": "DATEV (Summiert)",
        "DATEV export: one summarized booking per day and account.": "DATEV-Export: eine summierte Buchung pro Tag und Konto.",
        "Other": "Sonstige",
        "Time period": "Zeitraum",
        "All": "Alle",
        "Month": "Monat",
        "Select month": "Monat wählen",
        "Fields": "Felder",
        "Select all": "Alle auswählen",
        "Deselect all": "Auswahl aufheben",
        "Dismiss": "Abbrechen",
        "Export": "Exportieren",
        "Export created successfully!": "Export erfolgreich erstellt!",
        "Export could not be created.": "Export konnte nicht erstellt werden.",
        "No cash movements to export.": "Keine Kassenbewegungen zum Exportieren.",
        "No cash movements for {month}.": "Keine Kassenbewegungen für {month}.",
        "Name": "Name",
        "Created at": "Erstellt am",
        "Updated at": "Aktualisiert am",
        "Description": "Beschreibung",
        "Comment": "Kommentar",
        "Amount": "Betrag",
        "Direction": "Richtung",
        "Currency": "Währung",
        "Point of sale": "Verkaufsstelle",
        "Payment method": "Zahlungsart",
        "Administrator": "Administrator",
        "Cash report": "Kassenabschluss",
        "Purchase": "Bestellung"
    }
}
</i18n>

<script>
import _ from 'lodash'
import printJS from 'print-js-updated'
import { adminClient as apolloClient } from '@/store/apollo'

import { ensureFreshToken } from '@/helpers'
import { moneyFormat, timestamp2date } from '@/filters'

import router from '@/router'
import pinia from '@/pinia'
import { useSetupStore, useCashFlowLogStore } from '@/stores'

import PaginatedItems from '@/components/PaginatedItems'
import Tabs from '@/extensions/shop/views/Partials/SalesTabs'
import Error from '@/components/Error'

export default {
    name: 'CashFlowLogList',
    components: {
        PaginatedItems,
        Tabs,
        Error
    },
    filters: {
        moneyFormat,
        timestamp2date
    },
    data() {
        return {
            error: false,
            printing: false,
            disable: {
                showFilters: false
            },
            sortables: [
                'created_at',
                'updated_at',
                'name',
                'amount',
                'direction',
                'point_of_sale_name',
                'payment_method_name',
                'administrator_name'
            ],
            exportDialog: {
                visible: false,
                loading: false,
                processing: false,
                format: 'standard',
                dateRange: 'month',
                month: null,
                availableMonths: [],
                fields: ['id', 'created_at', 'name', 'amount', 'direction', 'payment_method_name', 'comment'],
                basicFields: [
                    { key: 'id', label: 'ID' },
                    { key: 'created_at', label: 'Created at' },
                    { key: 'updated_at', label: 'Updated at' },
                    { key: 'name', label: 'Name' },
                    { key: 'comment', label: 'Comment' },
                    { key: 'amount', label: 'Amount' },
                    { key: 'direction', label: 'Direction' },
                    { key: 'currency_code', label: 'Currency' },
                    { key: 'point_of_sale_name', label: 'Point of sale' },
                    { key: 'payment_method_name', label: 'Payment method' },
                    { key: 'administrator_name', label: 'Administrator' }
                ]
            },
            // ───────────────────────────────────────────────────────────────
            // DATEV-Export (bitte mit dem Steuerbüro abstimmen!).
            // Pro Tag:
            //   - 1 Zeile Gesamtumsatz Brutto -> Soll, Konto umsatz.konto (8400)
            //   - je Zahlungsart 1 Zeile -> Konto + S/H laut methods-Mapping
            // Das Mapping wird per Schlüsselwort auf payment_method_name gematcht
            // (erster Treffer gewinnt). Kontonummern bitte bestätigen/ergänzen.
            // ───────────────────────────────────────────────────────────────
            datev: {
                textPrefix: 'Kasse / ',
                currency: 'EUR',
                // Gesamtumsatz brutto (= kassierte Zahlungen + Rabatte) -> Soll, 8400.
                umsatz: { konto: '8400', sh: 'S', text: 'Kasse / Umsatz' },
                // Erlösschmälerung (Rabatte) je Tag -> Haben, 8736.
                discount: { konto: '8736', sh: 'H', text: 'Kasse / Erlösschmälerung' },
                // Manuelle Bar-Entnahmen aus der Kasse -> Soll, 1365 (Buchungstext = Kommentar).
                entnahme: { konto: '1365', sh: 'S', label: 'Kasse / Entnahme' },
                // Manuelle Bar-Einlagen in die Kasse -> Haben, 1890 (Privateinlage, bitte bestätigen).
                einlage: { konto: '1890', sh: 'H', label: 'Kasse / Einlage' },
                methods: [
                    { match: ['kredit', 'visa', 'master', 'maestro', 'ec', 'karte', 'card', 'überweisung', 'ueberweisung', 'bank', 'sepa'], konto: '1410', sh: 'H' },
                    { match: ['gutschein', 'voucher', 'geldwert'], konto: '1718', sh: 'S' },
                    { match: ['bar', 'cash'], konto: '1000', sh: 'H' }
                ],
                fallback: { konto: '1000', sh: 'H' }
            }
        }
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true)
            if (reload) this.initialize()
        }
    },
    computed: {
        setupStore() {
            return useSetupStore()
        },
        cashFlowLogStore() {
            return useCashFlowLogStore(pinia)
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions
        },
        loading() {
            return this.cashFlowLogStore.loading
        },
        cashFlowLogsPaginated() {
            return this.cashFlowLogStore.cashFlowLogsPaginated
        },
        allExportFieldsSelected() {
            return this.exportDialog.basicFields.every(f => this.exportDialog.fields.includes(f.key))
        },
        monthPickerOptions() {
            const available = this.exportDialog.availableMonths
            const now = new Date()
            const currentIndex = now.getFullYear() * 12 + now.getMonth()

            return {
                disabledDate: date => {
                    const index = date.getFullYear() * 12 + date.getMonth()
                    // Future months are not selectable.
                    if (index > currentIndex) return true

                    // Months without any cash movement are not selectable.
                    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                    return !available.includes(key)
                }
            }
        },
        showFilters() {
            return this.hasFilters || _.get(this.cashFlowLogsPaginated, 'showFilters') || false
        },
        hasFilters() {
            return !_.isEmpty(this.filters)
        },
        filters() {
            return _.cloneDeep(this.cashFlowLogsPaginated.filters)
        },
        sort() {
            const variables = this.cashFlowLogsPaginated.variables
            const defaultSort = this.cashFlowLogsPaginated.defaultSort || { field: 'created_at', direction: 'desc' }
            const order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field
            const direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction
            return { field: order, direction }
        }
    },
    methods: {
        initialize() {
            if (!this.enabledExtensions.includes('shop')) {
                this.updateLoading(true)
                return
            }

            this.updateLoading(true)
            this.refetch()
        },
        refetch(showSpinner = true) {
            if (showSpinner) this.updateLoading(true)

            return this.fetchCashFlowLogsPaginated()
                .then(() => this.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err

                    let code = 501
                    let message = 'An error occurred!'

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code
                        message = _.get(graphQLErrors, '0.message') || message
                    }

                    let notify = true
                    switch (code) {
                        case 401:
                            notify = false
                            this.error = { code, message: 'You are not authorized' }
                            break
                        case 403:
                            notify = false
                            this.error = { code, message: 'You are not allowed to be here' }
                            break
                        default:
                            this.error = true
                            break
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Cash movements could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        })
                    }

                    this.updateLoading(false)

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err)
                })
        },
        debouncedRefetch: _.debounce(function () {
            this.updateLoading(true)
            this.refetch()
        }, useSetupStore(pinia).debounce || 300),

        handleSizeChange(itemsPerPage) {
            this.updateLoading(true)
            this.defineItemsPerPage(itemsPerPage)
            this.refetch()
        },
        handleCurrentChange(currentPage) {
            this.updateLoading(true)
            this.defineCurrentPage(currentPage)
            this.refetch()
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns)
            apolloClient.clearStore()
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineFilterValue({ key, value })

            if (debounce) {
                this.debouncedRefetch()
            } else {
                this.updateLoading(true)
                this.refetch()
            }
        },
        handleSortInputChange(key, value) {
            this.defineSort({ key, value })
            this.refetch()
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (!visible) value = true

            this.disable.showFilters = true
            this.defineFilterVisibility(value)
            this.saveFilterVisibility({ scope: 'ex', key: 'cash_flow_log', value })
                .finally(() => this.disable.showFilters = false)
        },
        isCashReport(item) {
            return _.get(item, 'name') === 'cash report'
        },
        rowClass(item) {
            return this.isCashReport(item) ? 'cash-report-row' : ''
        },
        handleClick(item) {
            const id = _.get(item, 'purchase.id')
            if (!id) return
            this.goToPurchase(id)
        },
        goToPurchase(id) {
            router.push({ name: 'purchase-main', params: { id } }).catch(error => {
                const name = _.get(error, 'name', '')
                if (name !== 'NavigationDuplicated') console.error(error)
            })
        },
        async printCashReport(cashReport) {
            const file = _.get(cashReport, 'file')
            if (!file) return

            const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
            const table = 'files'
            const token = await ensureFreshToken()
            const printable = `${storageRoute}/${table}/${file.id}/${file.name}?token=${token}`

            this.printing = true
            printJS({
                printable,
                onError: () => {
                    this.printing = false
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Could not print the cash report PDF!'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    })
                },
                onPrintDialogClose: () => {
                    this.printing = false
                }
            })
        },

        openExportDialog() {
            this.exportDialog.visible = true
            this.loadExportData()
        },
        loadExportData() {
            this.exportDialog.loading = true

            // Lightweight scan (id + created_at only) to determine which months
            // contain cash flow. The full data is only loaded on export.
            this.fetchCashFlowLogDates()
                .then(timestamps => {
                    const months = new Set()
                    let latest = null

                    ;(timestamps || []).forEach(ts => {
                        if (!_.isNumber(ts)) return

                        const date = new Date(ts * 1000)
                        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                        months.add(key)

                        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1)
                        if (!latest || monthStart > latest) latest = monthStart
                    })

                    this.exportDialog.availableMonths = Array.from(months)
                    // Preselect the most recent month that contains cash flow.
                    this.exportDialog.month = latest
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err)

                    this.exportDialog.items = []
                    this.exportDialog.availableMonths = []
                    this.exportDialog.month = null

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Cash movements could not be fetched.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    })
                })
                .finally(() => {
                    this.exportDialog.loading = false
                })
        },
        toggleAllExportFields() {
            if (this.allExportFieldsSelected) {
                this.exportDialog.fields = []
            } else {
                this.exportDialog.fields = this.exportDialog.basicFields.map(f => f.key)
            }
        },
        getExportDateFilter() {
            if (this.exportDialog.dateRange !== 'month' || !this.exportDialog.month) return null

            const selected = new Date(this.exportDialog.month)
            // First day of the selected month at 00:00 until the last day at 23:59:59.
            const from = new Date(selected.getFullYear(), selected.getMonth(), 1, 0, 0, 0, 0)
            const to = new Date(selected.getFullYear(), selected.getMonth() + 1, 0, 23, 59, 59, 999)

            return {
                from: Math.floor(from.getTime() / 1000),
                to: Math.floor(to.getTime() / 1000)
            }
        },
        runExport() {
            this.exportDialog.processing = true

            // Only the full data is loaded here; for a selected month the date filter
            // keeps the payload small.
            this.fetchCashFlowLogsForExport({ dateFilter: this.getExportDateFilter() })
                .then(items => {
                    if (!items || !items.length) {
                        let title = this.$t('No cash movements to export.')

                        if (this.exportDialog.dateRange === 'month' && this.exportDialog.month) {
                            const month = timestamp2date(Math.floor(new Date(this.exportDialog.month).getTime() / 1000), 'MM.yyyy')
                            title = this.$t('No cash movements for {month}.', { month })
                        }

                        this.$notify.warning({
                            title,
                            iconClass: 'fas el-icon-fa-circle-info',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        })
                        return
                    }

                    if (this.exportDialog.format === 'datev') {
                        this.downloadDatevCsv(items)
                    } else {
                        this.downloadCsv(items)
                    }
                    this.exportDialog.visible = false

                    this.$notify.success({
                        title: this.$t('Export created successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    })
                })
                .catch(err => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err)

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Export could not be created.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    })
                })
                .finally(() => {
                    this.exportDialog.processing = false
                })
        },
        getExportValue(item, key) {
            switch (key) {
                case 'created_at':
                case 'updated_at': {
                    const value = item[key]
                    return value ? timestamp2date(value, this.$t('yyyy-MM-dd') + ' HH:mm') : ''
                }
                case 'direction':
                    return item.direction ? this.$t(item.direction) : ''
                case 'cash_report_id':
                    return _.get(item, 'cashReport.id', '')
                case 'purchase_id':
                    return _.get(item, 'purchase.id', '')
                default: {
                    const value = item[key]
                    return value === null || value === undefined ? '' : value
                }
            }
        },
        csvEscape(value, separator) {
            let str = String(value === null || value === undefined ? '' : value)
            if (str.includes('"') || str.includes(separator) || str.includes('\n')) {
                str = `"${str.replace(/"/g, '""')}"`
            }
            return str
        },
        exportFileName(prefix) {
            const pad = n => String(n).padStart(2, '0')
            if (this.exportDialog.dateRange === 'month' && this.exportDialog.month) {
                const d = new Date(this.exportDialog.month)
                return `${prefix}_${d.getFullYear()}-${pad(d.getMonth() + 1)}.csv`
            }
            const now = new Date()
            return `${prefix}_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.csv`
        },
        triggerCsvDownload(csv, fileName) {
            // BOM so Excel detects UTF-8 (umlauts).
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)

            const element = document.createElement('a')
            element.setAttribute('href', url)
            element.setAttribute('download', fileName)
            element.style.display = 'none'
            document.body.appendChild(element)
            element.click()
            document.body.removeChild(element)
            URL.revokeObjectURL(url)
        },
        downloadCsv(items) {
            const separator = ';'
            const fields = this.exportDialog.basicFields.filter(f => this.exportDialog.fields.includes(f.key))

            const header = fields.map(f => this.csvEscape(this.$t(f.label), separator)).join(separator)
            const rows = items.map(item =>
                fields.map(f => this.csvEscape(this.getExportValue(item, f.key), separator)).join(separator)
            )

            this.triggerCsvDownload([header, ...rows].join('\r\n'), this.exportFileName('cash-movements'))
        },
        formatDatevAmount(value) {
            return (Math.round((Number(value) || 0) * 100) / 100).toFixed(2).replace('.', ',')
        },
        resolveDatevMethod(name) {
            const n = String(name).toLowerCase()
            return this.datev.methods.find(rule => rule.match.some(keyword => n.includes(keyword))) || this.datev.fallback
        },
        downloadDatevCsv(items) {
            const separator = ';'

            // Group per day; track the receipts net per payment method and the
            // discounts per order.
            // IMPORTANT: `amount` is always a positive magnitude; the sign is carried
            // by `direction` ('in' adds, 'out' subtracts). We therefore net signed.
            // Discounts live on the order (purchase.discount_total) and are counted
            // once per order (split payments reference the same order).
            const days = {}
            const seenPurchases = new Set()
            items.forEach(item => {
                const magnitude = Math.abs(Number(item.amount) || 0)
                if (!magnitude || !_.isNumber(item.created_at)) return

                const date = timestamp2date(item.created_at, 'dd.MM.yyyy')
                if (!days[date]) {
                    days[date] = { ts: item.created_at, date, sum: 0, discount: 0, methods: {}, movements: [] }
                } else if (item.created_at < days[date].ts) {
                    days[date].ts = item.created_at
                }
                const day = days[date]

                // Manual cash deposits/withdrawals: no related order and not a cash
                // report. They are NOT revenue, so they are kept out of the sales
                // aggregation and listed individually (with their comment as text).
                const isManualCashMovement = !_.get(item, 'purchase.id') && item.name !== 'cash report'
                if (isManualCashMovement) {
                    day.movements.push({
                        direction: item.direction,
                        amount: magnitude,
                        comment: String(item.comment || '').trim()
                    })
                    return
                }

                const signed = item.direction === 'out' ? -magnitude : magnitude
                day.sum += signed

                const method = item.payment_method_name || item.name || this.$t('Other')
                day.methods[method] = (day.methods[method] || 0) + signed

                // Discount of the related order, counted once per order.
                const purchaseId = _.get(item, 'purchase.id')
                const purchaseDiscount = Math.abs(Number(_.get(item, 'purchase.discount_total')) || 0)
                if (purchaseId && purchaseDiscount && !seenPurchases.has(purchaseId)) {
                    seenPurchases.add(purchaseId)
                    day.discount += purchaseDiscount
                }
            })

            const currency = (items.find(item => item.currency_code) || {}).currency_code || this.datev.currency
            const flip = sh => (sh === 'S' ? 'H' : 'S')

            const header = ['Umsatz', 'S/H', 'Währung', 'Datum', 'Konto', 'Buchungstext'].join(separator)
            const rows = []
            // DATEV amounts are unsigned; a negative net flips the S/H indicator.
            const push = (net, baseSh, date, konto, text) => {
                const cents = Math.round(net * 100)
                if (cents === 0) return
                const sh = cents > 0 ? baseSh : flip(baseSh)
                rows.push([this.formatDatevAmount(Math.abs(cents) / 100), sh, currency, date, konto, text]
                    .map(v => this.csvEscape(v, separator)).join(separator))
            }

            Object.values(days)
                .sort((a, b) => a.ts - b.ts)
                .forEach(day => {
                    // Gesamtumsatz brutto (= kassierte Zahlungen + Rabatte) -> Soll, 8400.
                    push(day.sum + day.discount, this.datev.umsatz.sh, day.date, this.datev.umsatz.konto, this.datev.umsatz.text)

                    // Erlösschmälerung (Rabatte) -> Haben, 8736.
                    push(day.discount, this.datev.discount.sh, day.date, this.datev.discount.konto, this.datev.discount.text)

                    // One row per payment method with its own DATEV account and S/H.
                    Object.keys(day.methods)
                        .sort()
                        .forEach(method => {
                            const rule = this.resolveDatevMethod(method)
                            push(day.methods[method], rule.sh, day.date, rule.konto, `${this.datev.textPrefix}${method}`)
                        })

                    // Manual cash deposits/withdrawals, one row each (comment as text).
                    day.movements.forEach(movement => {
                        const rule = movement.direction === 'out' ? this.datev.entnahme : this.datev.einlage
                        push(movement.amount, rule.sh, day.date, rule.konto, movement.comment || rule.label)
                    })
                })

            this.triggerCsvDownload([header, ...rows].join('\r\n'), this.exportFileName('datev-kasse'))
        },
        fetchCashFlowLogDates() {
            return this.cashFlowLogStore.fetchCashFlowLogDates()
        },
        fetchCashFlowLogsForExport(options) {
            return this.cashFlowLogStore.fetchCashFlowLogsForExport(options)
        },

        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload)
        },
        fetchCashFlowLogsPaginated() {
            return this.cashFlowLogStore.fetchCashFlowLogsPaginated()
        },
        updateLoading(value) {
            this.cashFlowLogStore.updateLoading(value)
        },
        defineItemsPerPage(value) {
            this.cashFlowLogStore.defineItemsPerPage(value)
        },
        defineCurrentPage(value) {
            this.cashFlowLogStore.defineCurrentPage(value)
        },
        defineActiveColumns(value) {
            this.cashFlowLogStore.defineActiveColumns(value)
        },
        defineFilterValue(payload) {
            this.cashFlowLogStore.defineFilterValue(payload)
        },
        defineFilterVisibility(value) {
            this.cashFlowLogStore.defineFilterVisibility(value)
        },
        defineSort(payload) {
            this.cashFlowLogStore.defineSorting(payload)
        }
    },
    mounted() {
        this.initialize()
    },
    beforeDestroy() {
        this.cashFlowLogStore.resetCashFlowLogsPaginated()
    }
}
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.cash-flow-log-list:deep()
    .column
        &.created_at,
        &.updated_at,
        &.point_of_sale_name,
        &.payment_method_name
            white-space: nowrap

    tr.main-item.cash-report-row
        color: #6E26FF
        .column,
        .amount,
        .amount.in,
        .amount.out,
        .direction,
        .direction.in,
        .direction.out
            color: #6E26FF
            font-weight: 600

.amount
    &.out
        color: $--color-danger
    &.in
        color: darken($--color-success, 15%)
.direction
    &.out
        color: $--color-danger
    &.in
        color: darken($--color-success, 15%)
.gray
    color: lighten($color-grey-text, 10%)

.table-top .row + .row
    margin-top: 0.5rem
</style>

<style lang="sass">
.error
    padding: 20px

.cash-flow-log-export-dialog
    .export-fields
        .field-section-label
            font-weight: 600
            margin-bottom: 8px
        .export-format
            margin-bottom: 20px
        .date-range
            display: flex
            align-items: center
            gap: 12px
            margin-bottom: 20px
        .datev-hint
            color: #909399
            font-size: 12px
            line-height: 1.4
        .field-section-header
            display: flex
            align-items: center
            justify-content: space-between
            margin-bottom: 8px
            .field-section-label
                margin-bottom: 0
        .field-chips
            display: flex
            flex-wrap: wrap
            gap: 8px
            .el-checkbox
                margin: 0
</style>
